<template>
  <div class="performance-dashboard-page">

    <!-- ══ Access denied ══════════════════════════════════════════════ -->
    <b-alert v-if="!maxScope" show variant="warning" class="mt-2">
      <feather-icon icon="LockIcon" size="16" class="mr-50" />
      You do not have permission to view performance data.
      Contact your administrator to request access.
    </b-alert>

    <template v-else>

    <!-- ══ Toolbar (navbar already shows pageTitle; scope badge removed — buttons convey active scope) ══ -->
    <div class="d-flex align-items-center justify-content-between flex-wrap performance-dashboard-toolbar">
      <small class="text-muted">{{ headerSubtitle }}</small>

      <div class="d-flex flex-wrap align-items-center" style="gap:6px">
        <!-- Scope switcher (only shown when user has more than one scope available) -->
        <div v-if="availableScopes.length > 1" class="d-flex flex-wrap" style="gap:4px">
          <b-button
            v-for="s in availableScopes"
            :key="s.value"
            size="sm"
            :variant="activeScope === s.value ? s.variant : `outline-${s.variant}`"
            @click="switchScope(s.value)"
          >
            <feather-icon :icon="s.icon" size="13" class="mr-25" />
            <span class="d-none d-sm-inline">{{ s.label }}</span>
            <span class="d-sm-none">{{ s.label.split(' ')[0] }}</span>
          </b-button>
        </div>

        <b-button variant="outline-secondary" size="sm" :disabled="loading" @click="loadData">
          <b-spinner v-if="loading" small class="mr-50" />
          <feather-icon v-else icon="RefreshCwIcon" size="14" class="mr-50" />
          <span class="d-none d-sm-inline">Refresh</span>
        </b-button>
      </div>
    </div>

    <!-- ── Filters ── -->
    <perf-filter-bar
      :filter-from="filterFrom"
      :filter-to="filterTo"
      :filter-vertical="filterVertical"
      :filter-team-lead="filterTeamLead"
      :recruiter-search="recruiterSearch"
      :active-only="activeOnly"
      :active-period="activePeriod"
      :loading="loading"
      :presets="presets"
      :vertical-options="verticalOptions"
      :team-lead-options="teamLeadOptions"
      :hide-recruiter-filter="activeScope === 'self'"
      :hide-team-lead-filter="activeScope === 'self' || activeScope === 'team'"
      @preset="applyPreset"
      @update-filter="onFilterUpdate"
      @apply="loadData"
    />

    <!-- ── Error ── -->
    <b-alert
      v-if="error"
      show
      variant="danger"
      class="mb-2"
      dismissible
      @dismissed="error = null"
    >
      {{ error }}
    </b-alert>

    <!-- ── Global loading ── -->
    <div v-if="loading && !rawRows.length" class="text-center py-5">
      <b-spinner variant="primary" style="width:3rem;height:3rem;" />
      <p class="mt-1 text-muted">Loading performance data…</p>
    </div>

    <template v-else>

      <!-- ── KPI Cards ── -->
      <perf-kpi-cards :cards="kpiCards" class="mb-2" />

      <!-- ── Charts ── -->
      <perf-charts
        :filtered-rows="filteredRows"
        :filtered-raw="filteredRaw"
        :scope="activeScope"
        class="mb-2"
      />

      <!-- ── Data Tables ── -->
      <perf-data-tabs
        :filtered-rows="filteredRows"
        :filtered-monthly-rows="filteredMonthlyRows"
        :filtered-raw="filteredRaw"
        :filtered-event-rows="filteredEventRows"
        :event-loading="eventLoading"
        :event-error="eventError"
        :metric-reports="metricReports"
        :pivot-months="pivotMonths"
        :filter-from="filterFrom"
        :filter-to="filterTo"
        :scope="activeScope"
      />

    </template>

    </template><!-- /maxScope -->
    </div>
  </template>
  
  <script>
import moment from 'moment'
import { BButton, BSpinner, BAlert } from 'bootstrap-vue'
import performanceService from '@/services/performance-service'
import { formatRevenue } from './perf-utils'
  import {
  PERFORMANCE_PERMISSIONS,
  resolvePerformanceScope,
  SCOPE_META,
} from '@/utils/performance-permissions'

import PerfFilterBar from './components/PerfFilterBar.vue'
import PerfKpiCards  from './components/PerfKpiCards.vue'
import PerfCharts    from './components/PerfCharts.vue'
import PerfDataTabs  from './components/PerfDataTabs.vue'
  
  export default {
  name: 'PerformanceDashboard',
    components: {
    BButton, BSpinner, BAlert,
    PerfFilterBar,
    PerfKpiCards,
    PerfCharts,
    PerfDataTabs,
  },

    data() {
    // Read logged-in user info from localStorage
    let currentUser = null
    try {
      const ud = JSON.parse(localStorage.getItem('userData') || '{}')
      currentUser = {
        emp_id:    ud.emp_id    || ud.empcode || '',
        name:      ud.name      || `${ud.first_name || ''} ${ud.last_name || ''}`.trim(),
        firstName: ud.first_name || '',
        lastName:  ud.last_name  || '',
      }
    } catch (_) { /* ignore */ }

      return {
      // ── Logged-in user ────────────────────────────────────────────
      currentUser,

      // ── Active scope ('org' | 'team' | 'self') ───────────────────
      // Computed maxScope drives the initial value; set in created()
      activeScope: null,

      // ── Filter state ─────────────────────────────────────────────
      filterFrom:      moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      filterTo:        moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
      filterVertical:  'Both',
      filterTeamLead:  '',
      recruiterSearch: '',
      activeOnly:      false,
      activePeriod:    'last_month',

      // ── API data ─────────────────────────────────────────────────
      rawRows: [],
      loading: false,
      error:   null,

      // Event-level extract from /recruiter-performance/raw (not derived from summary)
      eventRows:    [],
      eventLoading: false,
      eventError:   null,

      // ── Static option lists ───────────────────────────────────────
      presets: [
        { value: 'ytd',          label: 'YTD' },
        { value: 'this_month',   label: 'This Month' },
        { value: 'last_month',   label: 'Last Month' },
        { value: 'last_quarter', label: 'Last Quarter' },
        { value: 'this_quarter', label: 'This Quarter' },
      ],
      verticalOptions: [
        { value: 'Both',             text: 'Both Verticals' },
        { value: 'Infra Industry',   text: 'Infra Industry' },
        { value: 'Service Industry', text: 'Service Industry' },
      ],

      // ── Scope metadata (imported constant) ───────────────────────
      scopeMeta: SCOPE_META,
    }
  },

  computed: {
    // ── Highest permission tier the user holds ─────────────────────
    maxScope() {
      return resolvePerformanceScope(this.$can)
    },

    // ── Ordered list of scopes this user can switch between ────────
    availableScopes() {
      const all = [
        { value: 'org',  label: 'Organisation',   icon: 'GlobeIcon',  variant: 'primary' },
        { value: 'team', label: 'My Team',         icon: 'UsersIcon',  variant: 'warning' },
        { value: 'self', label: 'My Performance',  icon: 'UserIcon',   variant: 'success' },
      ]
      const rank = { org: 0, team: 1, self: 2 }
      const maxRank = rank[this.maxScope] ?? 99
      // Only include scopes the user is allowed to access
      return all.filter(s => rank[s.value] >= maxRank)
    },

    // ── Scope-filtered raw rows (before additional UI filters) ─────
    scopeFilteredRaw() {
      const scope = this.activeScope || this.maxScope
      if (!scope || scope === 'org') return this.rawRows

      if (scope === 'self') {
        const myId   = this.currentUser?.emp_id || ''
        const myName = this.currentUser?.name?.toLowerCase() || ''
        return this.rawRows.filter(r =>
          (myId && r.emp_id === myId) ||
          (myName && r.recruiter_name?.toLowerCase() === myName)
        )
      }

      if (scope === 'team') {
        // Team Lead's own name in the 'Team Lead' column
        const myName = this.currentUser?.name?.toLowerCase() || ''
        const myId   = this.currentUser?.emp_id || ''
        return this.rawRows.filter(r => {
          const tl = (r['Team Lead'] || '').toLowerCase()
          return tl && (tl === myName || r.emp_id === myId)
        })
      }

      return this.rawRows
    },

    // ── Filtered raw rows (scope + UI filters) ────────────────────
    filteredRaw() {
      let rows = [...this.scopeFilteredRaw]
      if (this.activeOnly)     rows = rows.filter(r => r.employee_status === 'ACTIVE')
      if (this.filterTeamLead) rows = rows.filter(r => r['Team Lead'] === this.filterTeamLead)
      if (this.recruiterSearch) {
        const q = this.recruiterSearch.toLowerCase()
        rows = rows.filter(r => r.recruiter_name?.toLowerCase().includes(q))
      }
      return rows
    },

    // Event-level rows from /raw, scoped with the same UI filters as the summary.
    // Never exploded from aggregated KPIs — empty when the extract is missing.
    filteredEventRows() {
      let rows = [...this.eventRows]
      const scope = this.activeScope || this.maxScope
      const scopedEmpIds = new Set(this.scopeFilteredRaw.map(r => r.emp_id).filter(Boolean))
      const scopedNames = new Set(
        this.scopeFilteredRaw.map(r => (r.recruiter_name || '').toLowerCase()).filter(Boolean),
      )

      if (scope === 'self') {
        const myId   = this.currentUser?.emp_id || ''
        const myName = this.currentUser?.name?.toLowerCase() || ''
        rows = rows.filter(r =>
          (myId && r.emp_id === myId) ||
          (myName && r.recruiter_name?.toLowerCase() === myName)
        )
      } else if (scope === 'team') {
        if (!scopedEmpIds.size && !scopedNames.size) return []
        rows = rows.filter(r =>
          (r.emp_id && scopedEmpIds.has(r.emp_id)) ||
          (r.recruiter_name && scopedNames.has(String(r.recruiter_name).toLowerCase()))
        )
      }

      if (this.activeOnly || this.filterTeamLead) {
        const allowedIds = new Set(this.filteredRaw.map(r => r.emp_id).filter(Boolean))
        const allowedNames = new Set(
          this.filteredRaw.map(r => (r.recruiter_name || '').toLowerCase()).filter(Boolean),
        )
        rows = rows.filter(r =>
          (r.emp_id && allowedIds.has(r.emp_id)) ||
          (r.recruiter_name && allowedNames.has(String(r.recruiter_name).toLowerCase()))
        )
      }

      if (this.recruiterSearch) {
        const q = this.recruiterSearch.toLowerCase()
        rows = rows.filter(r => r.recruiter_name?.toLowerCase().includes(q))
      }
      return rows
    },

    // Aggregated per recruiter × vertical
    filteredRows() {
      const map = {}
      this.filteredRaw.forEach(r => {
        const key = `${r.emp_id}__${r.job_vertical}`
        if (!map[key]) {
          map[key] = {
            emp_id:               r.emp_id,
            recruiter_name:       r.recruiter_name,
            team_lead:            r['Team Lead'] || '—',
            job_vertical:         r.job_vertical,
            employee_status:      r.employee_status,
            profile_shared_count: 0,
            shortlisted_count:    0,
            offered_count:        0,
            joiners_count:        0,
            drops_count:          0,
            total_revenue:        0,
          }
        }
        const rec = map[key]
        rec.profile_shared_count += +r.profile_shared_count || 0
        rec.shortlisted_count    += +r.shortlisted_count    || 0
        rec.offered_count        += +r.offered_count        || 0
        rec.joiners_count        += +r.joiners_count        || 0
        rec.drops_count          += +r.drops_count          || 0
        rec.total_revenue        += +r.total_revenue        || 0
      })
      return Object.values(map)
    },

    // One row per recruiter × month × vertical (API returns separate rows per client_name)
    filteredMonthlyRows() {
      const map = {}
      this.filteredRaw.forEach(r => {
        const key = `${r.emp_id}__${r.month}__${r.job_vertical}`
        if (!map[key]) {
          map[key] = {
            month:                r.month,
            emp_id:               r.emp_id,
            recruiter_name:       r.recruiter_name,
            team_lead:            r['Team Lead'] || '—',
            job_vertical:         r.job_vertical,
            employee_status:      r.employee_status,
            profile_shared_count: 0,
            shortlisted_count:    0,
            offered_count:        0,
            joiners_count:        0,
            drops_count:          0,
            total_revenue:        0,
          }
        }
        const rec = map[key]
        rec.profile_shared_count += +r.profile_shared_count || 0
        rec.shortlisted_count    += +r.shortlisted_count    || 0
        rec.offered_count        += +r.offered_count        || 0
        rec.joiners_count        += +r.joiners_count        || 0
        rec.drops_count          += +r.drops_count          || 0
        rec.total_revenue        += +r.total_revenue        || 0
      })
      return Object.values(map).sort((a, b) => (
        String(a.month).localeCompare(String(b.month))
        || String(a.emp_id).localeCompare(String(b.emp_id))
        || String(a.job_vertical || '').localeCompare(String(b.job_vertical || ''))
      ))
    },

    // ── KPI summary cards ─────────────────────────────────────────
    kpiCards() {
      const rows = this.filteredRaw
      const sum  = k => rows.reduce((a, r) => a + (+r[k] || 0), 0)
      return [
        { key: 'ps',      label: 'Profile Shared', value: sum('profile_shared_count'), icon: 'UsersIcon',        color: 'primary' },
        { key: 'sl',      label: 'Shortlisted',    value: sum('shortlisted_count'),    icon: 'UserCheckIcon',    color: 'info'    },
        { key: 'offered', label: 'Offered',         value: sum('offered_count'),        icon: 'BriefcaseIcon',    color: 'warning' },
        { key: 'joiners', label: 'Joiners',          value: sum('joiners_count'),        icon: 'TrendingUpIcon',   color: 'success' },
        { key: 'drops',   label: 'Drops',            value: sum('drops_count'),          icon: 'TrendingDownIcon', color: 'danger'  },
        { key: 'revenue', label: 'Revenue',          value: formatRevenue(sum('total_revenue')), icon: 'DollarSignIcon', color: 'success' },
      ]
    },

    // ── Team Lead dropdown options from live data ─────────────────
    teamLeadOptions() {
      const leads = [...new Set(this.rawRows.map(r => r['Team Lead']).filter(Boolean))].sort()
      return [{ value: '', text: 'All Team Leads' }, ...leads.map(l => ({ value: l, text: l }))]
    },

    // ── Header subtitle ───────────────────────────────────────────
    headerSubtitle() {
      if (!this.filterFrom || !this.filterTo) return ''
      const from = moment(this.filterFrom).format('DD MMM YYYY')
      const to   = moment(this.filterTo).format('DD MMM YYYY')
      return `${from}  →  ${to}   ·   ${this.filterVertical}`
    },

    // ── Unique sorted months (for pivot columns) ──────────────────
    pivotMonths() {
      return [...new Set(this.filteredRaw.map(r => r.month))].sort()
    },

    // ── Per-metric pivot reports ──────────────────────────────────
    metricReports() {
      const months  = this.pivotMonths
      const metaDefs = [
        { key: 'revenue',     tabLabel: 'Revenue',     label: 'Revenue',     metricField: 'total_revenue',     color: 'success', icon: 'DollarSignIcon',   isRevenue: true  },
        { key: 'joiners',     tabLabel: 'Joiners',     label: 'Joiners',     metricField: 'joiners_count',     color: 'success', icon: 'TrendingUpIcon',   isRevenue: false },
        { key: 'offered',     tabLabel: 'Offered',     label: 'Offered',     metricField: 'offered_count',     color: 'warning', icon: 'BriefcaseIcon',    isRevenue: false },
        { key: 'shortlisted', tabLabel: 'Shortlisted', label: 'Shortlisted', metricField: 'shortlisted_count', color: 'info',    icon: 'UserCheckIcon',    isRevenue: false },
        { key: 'drops',       tabLabel: 'Drops',       label: 'Drops',       metricField: 'drops_count',       color: 'danger',  icon: 'TrendingDownIcon', isRevenue: false },
      ]

      return metaDefs.map(meta => {
        const map         = {}
        const monthTotals = {}
        months.forEach(m => { monthTotals[m] = 0 })

        this.filteredRaw.forEach(r => {
          const key = `${r.emp_id}__${r.job_vertical}`
          if (!map[key]) {
            map[key] = { emp_id: r.emp_id, recruiter_name: r.recruiter_name, team_lead: r['Team Lead'] || '—', job_vertical: r.job_vertical, total: 0 }
            months.forEach(m => { map[key][`m_${m}`] = 0 })
          }
          const val = +r[meta.metricField] || 0
          map[key][`m_${r.month}`] = (map[key][`m_${r.month}`] || 0) + val
          map[key].total           += val
          if (r.month in monthTotals) monthTotals[r.month] += val
        })

        const rows       = Object.values(map).sort((a, b) => b.total - a.total)
        const grandTotal = rows.reduce((s, r) => s + r.total, 0)

        const fields = [
          { key: 'recruiter_name', label: 'Recruiter', sortable: true },
          { key: 'team_lead',      label: 'TL',        sortable: true },
          { key: 'job_vertical',   label: 'Vertical',  sortable: true },
          ...months.map(m => ({
            key:      `m_${m}`,
            label:    moment(m, 'YYYY-MM').format("MMM 'YY"),
            sortable: true,
            thClass:  'text-center',
            tdClass:  'text-center',
          })),
          { key: 'total', label: 'Total', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
        ]

        return {
          ...meta,
          fields,
          rows,
          monthTotals,
          grandTotal,
          grandTotalFormatted: meta.isRevenue ? formatRevenue(grandTotal) : grandTotal,
        }
      })
    },
  },

  created() {
    // Resolve initial scope from permissions (highest allowed)
    this.activeScope = this.maxScope
    if (this.activeScope) this.loadData()
  },

  methods: {
    /** Switch the visible scope (org → team → self) */
    switchScope(scope) {
      this.activeScope = scope
      // For self/team scope, pre-fill the TL filter if switching to team
      if (scope === 'team') {
        this.filterTeamLead = ''  // team scope handles filtering via scopeFilteredRaw
      }
    },

    async loadData() {
      if (!this.filterFrom || !this.filterTo) return
      this.loading = true
      this.eventLoading = true
      this.error = null
      this.eventError = null

      const summaryPromise = performanceService.getPerformanceData(
        this.filterFrom,
        this.filterTo,
        this.filterVertical,
      )
      const rawPromise = performanceService.getPerformanceRawData(
        this.filterFrom,
        this.filterTo,
        this.filterVertical,
      )

      try {
        this.rawRows = await summaryPromise
      } catch (e) {
        this.error = e?.response?.data?.message || e.message || 'Failed to load performance data'
      } finally {
        this.loading = false
      }

      try {
        this.eventRows = await rawPromise
      } catch (e) {
        this.eventRows = []
        if (e?.code === 'RAW_NOT_DEPLOYED' || e?.response?.status === 404) {
          this.eventError = 'Raw extract not deployed — ATS has not shipped GET /api/dashboard/recruiter-performance/raw yet.'
        } else {
          this.eventError = e?.response?.data?.message || e.message || 'Failed to load raw event data'
        }
      } finally {
        this.eventLoading = false
      }
    },

    onFilterUpdate(key, value) {
      this[key] = value
    },

    applyPreset(preset) {
      this.activePeriod = preset
      const now = moment()
      const set = (from, to) => {
        this.filterFrom = from.format('YYYY-MM-DD')
        this.filterTo   = to.format('YYYY-MM-DD')
      }
      switch (preset) {
        case 'ytd':          set(now.clone().startOf('year'), now); break
        case 'this_month':   set(now.clone().startOf('month'), now.clone().endOf('month')); break
        case 'last_month':   set(now.clone().subtract(1,'month').startOf('month'), now.clone().subtract(1,'month').endOf('month')); break
        case 'last_quarter': set(now.clone().subtract(1,'quarter').startOf('quarter'), now.clone().subtract(1,'quarter').endOf('quarter')); break
        case 'this_quarter': set(now.clone().startOf('quarter'), now.clone().endOf('quarter')); break
        default: break
      }
    },
  },
}
  </script>

<style lang="scss">
// Navbar already shows pageTitle — reclaim the large content-wrapper gap
// (same pattern as Main Dashboard / TaskBoard). Scoped to this page only.
.performance-dashboard-page {
  margin-top: -0.85rem;
}

.performance-dashboard-toolbar {
  gap: 8px;
  margin-bottom: 0.5rem;
}
</style>
